import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Package, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Calendar,
  User,
  FileText,
  MessageSquare,
  Phone,
  MapPin
} from 'lucide-react';
import CommonHeader from '../CommonHeader';
import { OrderIdBadge } from '../shared';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';

interface TimelineEvent {
  id: string;
  status: 'completed' | 'in_progress' | 'pending';
  title: string;
  description: string;
  date: string;
  time: string;
  icon: React.ReactNode;
}

interface Order {
  id: string;
  order_number?: string;
  garmentType: string;
  description: string;
  status: string;
  orderDate: string;
  dueDate: string;
  amount: number;
  tailor: string;
  measurements: string[];
  tailorContact?: {
    phone: string;
    email: string;
    address: string;
  };
  progressSteps?: any[];
}

const OrderTimeline: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch order data from API
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setError(null);
        const apiUrl = API_BASE;

        const token = localStorage.getItem('authToken');
        
        let orderResponse;
        
        // Try authenticated endpoint first
        if (token) {
          try {
            orderResponse = await fetch(`${apiUrl}/api/orders/${id}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
            });
            
            // Check if response is ok, if not, we'll fall back to test endpoint
            if (!orderResponse.ok) {
              console.log(`Authenticated endpoint failed with status ${orderResponse.status}, falling back to test endpoint`);
              orderResponse = null;
            }
          } catch (fetchError) {
            console.log('Fetch error (likely network issue):', fetchError);
            orderResponse = null;
          }
        }
        
        if (orderResponse && orderResponse.ok) {
          const data = await orderResponse.json();
          console.log('Order API response:', data);
          console.log('Tailor contact from API:', data.tailorContact);
          console.log('Tailor field from API:', data.tailor);
          console.log('Full API data structure:', JSON.stringify(data, null, 2));
          
          const formattedOrder: Order = {
            id: data.id || id,
            order_number: data.order_number,
            garmentType: data.garmentType || 'Custom Garment',
            description: data.description || 'Custom order',
            status: data.status || 'pending',
            orderDate: data.orderDate || new Date().toISOString().split('T')[0],
            dueDate: data.dueDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            amount: data.amount || 0,
            tailor: typeof data.tailor === 'object' && data.tailor?.name 
              ? (typeof data.tailor.name === 'object' ? data.tailor.name.name : data.tailor.name)
              : (typeof data.tailor === 'string' ? data.tailor : 'Master Tailor Studio'),
            measurements: Array.isArray(data.measurements) ? data.measurements : ['Body Measurements'],
            tailorContact: (() => {
              // Try to get contact info from various possible locations
              const tailorName = data.tailor?.name;
              const tailorContact = data.tailorContact;
              
              if (typeof tailorName === 'object' && tailorName) {
                return {
                  phone: tailorName.phone || tailorName.whatsapp || '+91 98765 43210',
                  email: tailorName.email || data.tailor?.email || 'mastertailor@masterji.com',
                  address: tailorName.address || '123 Fashion Street, Mumbai, India 400001'
                };
              } else if (tailorContact) {
                return {
                  phone: tailorContact.phone || tailorContact.whatsapp || '+91 98765 43210',
                  email: tailorContact.email || 'mastertailor@masterji.com',
                  address: tailorContact.address || '123 Fashion Street, Mumbai, India 400001'
                };
              } else {
                return {
                  phone: '+91 98765 43210',
                  email: 'mastertailor@masterji.com',
                  address: '123 Fashion Street, Mumbai, India 400001'
                };
              }
            })(),
            progressSteps: data.progress_steps || []
          };
          
          setOrder(formattedOrder);
        } else {
          // Try test endpoint as fallback (now with authentication)
          const testResponse = await fetch(`${apiUrl}/api/test/orders/${id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
          });

          if (testResponse.ok) {
            const data = await testResponse.json();
            const formattedOrder: Order = {
              id: data.id || id,
              order_number: data.order_number,
              garmentType: data.garmentType || 'Custom Garment',
              description: data.description || 'Custom order',
              status: data.status || 'pending',
              orderDate: data.orderDate || new Date().toISOString().split('T')[0],
              dueDate: data.dueDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              amount: data.amount || 0,
              tailor: typeof data.tailor === 'object' && data.tailor?.name 
                ? (typeof data.tailor.name === 'object' ? data.tailor.name.name : data.tailor.name)
                : (typeof data.tailor === 'string' ? data.tailor : 'Master Tailor Studio'),
              measurements: Array.isArray(data.measurements) ? data.measurements : ['Body Measurements'],
              tailorContact: (() => {
                // Try to get contact info from various possible locations
                const tailorName = data.tailor?.name;
                const tailorContact = data.tailorContact;
                
                if (typeof tailorName === 'object' && tailorName) {
                  return {
                    phone: tailorName.phone || tailorName.whatsapp || '+91 98765 43210',
                    email: tailorName.email || data.tailor?.email || 'mastertailor@masterji.com',
                    address: tailorName.address || '123 Fashion Street, Mumbai, India 400001'
                  };
                } else if (tailorContact) {
                  return {
                    phone: tailorContact.phone || tailorContact.whatsapp || '+91 98765 43210',
                    email: tailorContact.email || 'mastertailor@masterji.com',
                    address: tailorContact.address || '123 Fashion Street, Mumbai, India 400001'
                  };
                } else {
                  return {
                    phone: '+91 98765 43210',
                    email: 'mastertailor@masterji.com',
                    address: '123 Fashion Street, Mumbai, India 400001'
                  };
                }
              })(),
              progressSteps: data.progress_steps || []
            };
            setOrder(formattedOrder);
          } else {
            // Handle authentication errors properly
            if (testResponse.status === 401 || testResponse.status === 403) {
              setError('Authentication required. Please log in again.');
            } else if (testResponse.status === 404) {
              setError('Order not found.');
            } else {
              setError('Failed to load order details. Please try again.');
            }
            setOrder(getFallbackOrder(id));
          }
        }
      } catch (_error) {
        console.error('Error fetching order:', error);
        setError('Failed to load order details. Showing sample data.');
        setOrder(getFallbackOrder(id));
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderData();
  }, [id]);

  // Fallback order data
  const getFallbackOrder = (orderId: string): Order => ({
    id: orderId,
    garmentType: 'Custom Garment',
    description: 'Custom order',
    status: 'pending',
    orderDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    amount: 15000,
    tailor: 'Master Tailor Studio',
    measurements: ['Body Measurements'],
    tailorContact: {
      phone: '+91 98765 43210',
      email: 'mastertailor@masterji.com',
      address: '123 Fashion Street, Mumbai, India 400001'
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-4">The order you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/customer/dashboard')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getTimelineEvents = (orderStatus: string, garmentType: string, progressSteps?: any[]) => {
    // If we have real progress steps from API, use them
    if (progressSteps && Array.isArray(progressSteps)) {
      return progressSteps.map((step, index) => {
        const getIcon = (stepName: string) => {
          if (stepName.toLowerCase().includes('order') || stepName.toLowerCase().includes('placed')) {
            return <Package className="w-5 h-5" />;
          } else if (stepName.toLowerCase().includes('measurement')) {
            return <FileText className="w-5 h-5" />;
          } else if (stepName.toLowerCase().includes('fabric') || stepName.toLowerCase().includes('selection')) {
            return <CheckCircle className="w-5 h-5" />;
          } else if (stepName.toLowerCase().includes('cutting')) {
            return <Clock className="w-5 h-5" />;
          } else if (stepName.toLowerCase().includes('stitching')) {
            return <Clock className="w-5 h-5" />;
          } else if (stepName.toLowerCase().includes('finishing')) {
            return <CheckCircle className="w-5 h-5" />;
          } else {
            return <AlertCircle className="w-5 h-5" />;
          }
        };

        const getDescription = (stepName: string) => {
          if (stepName.toLowerCase().includes('order') || stepName.toLowerCase().includes('placed')) {
            return 'Your order has been successfully placed and confirmed.';
          } else if (stepName.toLowerCase().includes('measurement')) {
            return 'Body measurements have been recorded and verified.';
          } else if (stepName.toLowerCase().includes('fabric') || stepName.toLowerCase().includes('selection')) {
            return `Premium fabric has been chosen for your ${garmentType.toLowerCase()}.`;
          } else if (stepName.toLowerCase().includes('cutting')) {
            return `Your ${garmentType.toLowerCase()} is being cut according to measurements.`;
          } else if (stepName.toLowerCase().includes('stitching')) {
            return `Your ${garmentType.toLowerCase()} is being stitched by our master craftsman.`;
          } else if (stepName.toLowerCase().includes('finishing')) {
            return `Final touches and finishing are being applied to your ${garmentType.toLowerCase()}.`;
          } else {
            return `${stepName} is in progress.`;
          }
        };

        return {
          id: (index + 1).toString(),
          status: step.status === 'completed' ? 'completed' as const : 
                 step.status === 'in_progress' ? 'in_progress' as const : 'pending' as const,
          title: step.step,
          description: getDescription(step.step),
          date: step.date ? new Date(step.date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          }) : 'TBD',
          time: step.date ? new Date(step.date).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          }) : 'TBD',
          icon: getIcon(step.step)
        };
      });
    }

    // Fallback to hardcoded timeline if no progress steps
    const baseEvents = [
      {
        id: '1',
        status: 'completed' as const,
        title: 'Order Placed',
        description: 'Your order has been successfully placed and confirmed.',
        date: '2025-08-10',
        time: '10:30 AM',
        icon: <Package className="w-5 h-5" />
      },
      {
        id: '2',
        status: 'completed' as const,
        title: 'Measurements Taken',
        description: 'Body measurements have been recorded and verified.',
        date: '2025-08-12',
        time: '2:15 PM',
        icon: <FileText className="w-5 h-5" />
      },
      {
        id: '3',
        status: 'completed' as const,
        title: 'Fabric Selected',
        description: `Premium fabric has been chosen for your ${garmentType.toLowerCase()}.`,
        date: '2025-08-14',
        time: '11:00 AM',
        icon: <CheckCircle className="w-5 h-5" />
      }
    ];

    // Add status-specific events
    if (orderStatus === 'new') {
      baseEvents.push({
        id: '4',
        status: 'pending' as const,
        title: 'Tailoring in Progress',
        description: `Your ${garmentType.toLowerCase()} will be tailored by our master craftsman.`,
        date: 'TBD',
        time: 'TBD',
        icon: <Clock className="w-5 h-5" />
      });
    } else if (orderStatus === 'in_progress') {
      baseEvents.push({
        id: '4',
        status: 'in_progress' as const,
        title: 'Tailoring in Progress',
        description: `Your ${garmentType.toLowerCase()} is currently being tailored by our master craftsman.`,
        date: '2025-08-16',
        time: '9:00 AM',
        icon: <Clock className="w-5 h-5" />
      });
    } else if (orderStatus === 'completed') {
      baseEvents.push({
        id: '4',
        status: 'completed' as const,
        title: 'Tailoring Completed',
        description: `Your ${garmentType.toLowerCase()} has been completed by our master craftsman.`,
        date: '2025-08-20',
        time: '3:00 PM',
        icon: <CheckCircle className="w-5 h-5" />
      });
    }

    // Add final events
    baseEvents.push(
      {
        id: '5',
        status: orderStatus === 'completed' ? 'completed' as const : 'pending' as const,
        title: 'Quality Check',
        description: 'Final quality inspection and finishing touches.',
        date: orderStatus === 'completed' ? '2025-08-21' : 'TBD',
        time: orderStatus === 'completed' ? '10:00 AM' : 'TBD',
        icon: <AlertCircle className="w-5 h-5" />
      },
      {
        id: '6',
        status: orderStatus === 'completed' ? 'completed' as const : 'pending' as const,
        title: 'Ready for Pickup',
        description: 'Your order is ready for pickup or delivery.',
        date: orderStatus === 'completed' ? '2025-08-22' : 'TBD',
        time: orderStatus === 'completed' ? '2:00 PM' : 'TBD',
        icon: <Calendar className="w-5 h-5" />
      }
    );

    return baseEvents;
  };

  const timelineEvents = getTimelineEvents(order.status, order.garmentType, order.progressSteps);

  // Get the current step name for display
  const getCurrentStepName = () => {
    if (order.progressSteps && Array.isArray(order.progressSteps)) {
      const currentStep = order.progressSteps.find(step => step.status === 'in_progress');
      if (currentStep) {
        return currentStep.step;
      }
      
      // If no in_progress step, find the first pending step
      const nextStep = order.progressSteps.find(step => step.status === 'pending');
      if (nextStep) {
        return `Next: ${nextStep.step}`;
      }
    }
    
    // Fallback to generic status
    return order.status.replace('_', ' ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColorForStep = (stepName: string) => {
    if (order.progressSteps && Array.isArray(order.progressSteps)) {
      const currentStep = order.progressSteps.find(step => step.status === 'in_progress');
      if (currentStep && currentStep.step === stepName) {
        return 'text-blue-600 bg-blue-100';
      }
    }
    return getStatusColor(order.status);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CommonHeader 
        title={`Order #${order.order_number ? order.order_number : order.id} Timeline`}
        showBackButton={true}
        backTo="/customer/dashboard"
      >
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate('/customer/dashboard')}
            className="p-2 text-gray-600 hover:text-gray-900"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        </div>
      </CommonHeader>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <p className="text-yellow-800 font-medium">Notice</p>
                <p className="text-yellow-700 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Order Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">{order.garmentType}</h2>
                <OrderIdBadge orderNumber={order.order_number} id={order.id} compact />
              </div>
              <p className="text-gray-600 mb-4">{order.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Ordered: {order.orderDate}
                </span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Due: {order.dueDate}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900 mb-2">₹{order.amount.toLocaleString('en-IN')}</div>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColorForStep(getCurrentStepName())}`}>
                {getStatusIcon(order.status)}
                <span className="ml-2 capitalize">{getCurrentStepName()}</span>
              </div>
            </div>
          </div>

          {/* Tailor Information */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Tailor Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">{order.tailor}</div>
                  <div className="text-sm text-gray-500">Master Tailor</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">{order.tailorContact?.phone || 'N/A'}</div>
                  <div className="text-sm text-gray-500">Phone</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">{order.tailorContact?.address || 'N/A'}</div>
                  <div className="text-sm text-gray-500">Address</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-6">Order Progress Timeline</h3>
          
          <div className="space-y-6">
            {timelineEvents.map((event, index) => (
              <div key={event.id} className="flex items-start space-x-4">
                {/* Timeline Line */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(event.status)}`}>
                    {event.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : event.status === 'in_progress' ? (
                      <Clock className="w-5 h-5 text-blue-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  {index < timelineEvents.length - 1 && (
                    <div className={`w-0.5 h-16 mt-2 ${
                      event.status === 'completed' ? 'bg-green-200' : 'bg-gray-200'
                    }`} />
                  )}
                </div>

                {/* Event Content */}
                <div className="flex-1 pb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{event.title}</h4>
                    <div className="text-sm text-gray-500">
                      {event.date} at {event.time}
                    </div>
                  </div>
                  <p className="text-gray-600">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <button className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
            <MessageSquare className="w-5 h-5" />
            <span>Message Tailor</span>
          </button>
          <button className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2">
            <Phone className="w-5 h-5" />
            <span>Call Tailor</span>
          </button>
        </motion.div>
      </main>
    </div>
  );
};

export default OrderTimeline;
